import { Component, OnInit, OnDestroy } from '@angular/core';
import { UserService } from '@/service/user.service';
import { GraphqlService } from '@/service/graphql.service';
import { AdvisoryBoardOffersFacade } from '@/app/store/advisory-board-offers/advisory-board-offers.facade';
import { selectUser } from '@/app/store/user/user.selectors';
import { Store } from '@ngrx/store';
import { Subject, takeUntil, combineLatest } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';
import { AsyncPipe } from '@angular/common';

import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { OfferCardComponent } from '@/app/components/offer-card/offer-card.component';
import { MatChipsModule } from '@angular/material/chips';
import { User } from '@/model/user';

@Component({
  selector: 'app-advisory-board-view',
  imports: [
    MatProgressSpinnerModule,
    MatIconModule,
    OfferCardComponent,
    MatChipsModule,
    AsyncPipe,
  ],
  templateUrl: './advisory-board-view.component.html',
  styleUrl: './advisory-board-view.component.scss',
  standalone: true,
})
export class AdvisoryBoardViewComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // Store observables
  readonly offers$ = this.advisoryBoardOffersFacade.offers$;
  readonly loading$ = this.advisoryBoardOffersFacade.loading$;
  readonly error$ = this.advisoryBoardOffersFacade.error$;
  readonly offersCount$ = this.advisoryBoardOffersFacade.offersCount$;

  // User and categories
  readonly user$ = this.store.select(selectUser);
  readonly advisoryCategories$ = this.user$.pipe(
    filter((user): user is User => !!user),
    map((user) => user?.advisoryCategories || []),
    map((categoryIds) =>
      this.graphqlService
        .getCurrentCategories()
        .filter((category) => categoryIds.includes(category.id)),
    ),
  );

  constructor(
    private userService: UserService,
    private graphqlService: GraphqlService,
    private advisoryBoardOffersFacade: AdvisoryBoardOffersFacade,
    private store: Store,
  ) {}

  ngOnInit(): void {
    // Load user info and categories, then load offers
    combineLatest([
      this.user$.pipe(
        filter((user): user is User => !!user),
        take(1),
      ),
      this.graphqlService.allCategories().pipe(take(1)),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ([user]) => {
          if (user?.advisoryCategories && user.advisoryCategories.length > 0) {
            // Load offers for the user's advisory categories
            this.advisoryBoardOffersFacade.loadAdvisoryBoardOffers(
              user.advisoryCategories,
            );
          }
        },
        error: (error) => {
          console.error('Error loading user info or categories:', error);
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();

    // Clear offers when component is destroyed
    this.advisoryBoardOffersFacade.clearAdvisoryBoardOffers();
  }
}
